import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ActivityBoardPermissionItem, HolidayItem } from 'library/types';

dayjs.extend(customParseFormat);

export const DATE_KEY_FORMAT = 'YYYY-MM-DD';
const PUBLIC_HOLIDAY_DATE_FORMAT = 'DD.MM.YYYY';
const HALF_DAY_MIN_HOURS = 3.5;
const HALF_DAY_MAX_HOURS = 4;

export type PermissionDayGroup = {
  assignedUserId: number;
  nameSurname: string;
  entries: ActivityBoardPermissionItem[];
  totalHours: number;
  isHalfDay: boolean;
};

export type CalendarDayEvents = {
  holidays: HolidayItem[];
  permissions: PermissionDayGroup[];
};

export type CalendarEventMap = Map<string, CalendarDayEvents>;
export type CalendarViewMode = 'calendarYear' | 'rollingYear' | 'currentMonth';

type MutableCalendarDayEvents = {
  holidays: HolidayItem[];
  permissionEntries: ActivityBoardPermissionItem[];
};

const isExactMidnight = (date: Dayjs) => date.valueOf() === date.startOf('day').valueOf();

const getEntryHours = (entry: ActivityBoardPermissionItem) => {
  if (Number.isFinite(entry.estimatedHours) && entry.estimatedHours > 0) return entry.estimatedHours;

  const start = dayjs(entry.startDate);
  const end = dayjs(entry.endDate);

  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return 0;

  return end.diff(start, 'minute') / 60;
};

const isHalfDayHours = (hours: number) => hours >= HALF_DAY_MIN_HOURS && hours <= HALF_DAY_MAX_HOURS;

const getPermissionEndDay = (start: Dayjs, end: Dayjs) => {
  // Treat a range ending exactly at 00:00 as end-exclusive so the next day is not marked.
  if (end.isAfter(start) && isExactMidnight(end)) return end.subtract(1, 'millisecond').startOf('day');

  return end.startOf('day');
};

const getOrCreateDay = (map: Map<string, MutableCalendarDayEvents>, dateKey: string) => {
  const existing = map.get(dateKey);

  if (existing) return existing;

  const day: MutableCalendarDayEvents = { holidays: [], permissionEntries: [] };
  map.set(dateKey, day);

  return day;
};

export const getMonthCells = (year: number, month: number): Array<Dayjs | null> => {
  const monthStart = dayjs().year(year).startOf('year').month(month).startOf('month');
  const mondayFirstOffset = (monthStart.day() + 6) % 7;
  const daysInMonth = monthStart.daysInMonth();

  return Array.from({ length: 42 }, (_, index) => {
    const dayOfMonth = index - mondayFirstOffset + 1;

    return dayOfMonth > 0 && dayOfMonth <= daysInMonth ? monthStart.date(dayOfMonth) : null;
  });
};

export const getVisibleMonths = (
  viewMode: CalendarViewMode,
  selectedYear: number,
  referenceDate: Dayjs = dayjs()
): Dayjs[] => {
  const referenceMonth = referenceDate.startOf('month');

  if (viewMode === 'currentMonth') return [referenceMonth];

  if (viewMode === 'rollingYear') {
    return Array.from({ length: 12 }, (_, monthOffset) => referenceMonth.add(monthOffset, 'month'));
  }

  const yearStart = referenceMonth.year(selectedYear).startOf('year');

  return Array.from({ length: 12 }, (_, month) => yearStart.month(month));
};

export const buildCalendarEventMapForRange = (
  permissionItems: ActivityBoardPermissionItem[],
  holidays: HolidayItem[],
  startDate: Dayjs,
  endDate: Dayjs,
  assignedUserId: number | null = null
): CalendarEventMap => {
  const mutableMap = new Map<string, MutableCalendarDayEvents>();
  const rangeStart = startDate.startOf('day');
  const rangeEnd = endDate.endOf('day');

  if (!rangeStart.isValid() || !rangeEnd.isValid() || rangeEnd.isBefore(rangeStart)) return new Map();

  holidays.forEach((holiday) => {
    const holidayDate = dayjs(holiday.localeDateString, PUBLIC_HOLIDAY_DATE_FORMAT, true);

    if (
      !holidayDate.isValid() ||
      holidayDate.isBefore(rangeStart, 'day') ||
      holidayDate.isAfter(rangeEnd, 'day')
    ) {
      return;
    }

    const dateKey = holidayDate.format(DATE_KEY_FORMAT);
    const day = getOrCreateDay(mutableMap, dateKey);

    if (!day.holidays.some((item) => item.id === holiday.id && item.title === holiday.title)) {
      day.holidays.push(holiday);
    }
  });

  permissionItems.forEach((permission) => {
    if (assignedUserId !== null && permission.assignedUserId !== assignedUserId) return;

    const start = dayjs(permission.startDate);
    const end = dayjs(permission.endDate);

    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return;

    const permissionStart = start.startOf('day');
    const permissionEnd = getPermissionEndDay(start, end);

    if (permissionEnd.isBefore(rangeStart, 'day') || permissionStart.isAfter(rangeEnd, 'day')) return;

    let cursor = permissionStart.isBefore(rangeStart, 'day') ? rangeStart : permissionStart;
    const clippedEnd = permissionEnd.isAfter(rangeEnd, 'day') ? rangeEnd : permissionEnd;

    while (cursor.isBefore(clippedEnd, 'day') || cursor.isSame(clippedEnd, 'day')) {
      const isWeekend = cursor.day() === 0 || cursor.day() === 6;
      const dateKey = cursor.format(DATE_KEY_FORMAT);

      if (!isWeekend) {
        const day = getOrCreateDay(mutableMap, dateKey);

        if (
          !day.permissionEntries.some(
            (item) =>
              item.id === permission.id &&
              item.assignedUserId === permission.assignedUserId &&
              item.startDate === permission.startDate &&
              item.endDate === permission.endDate
          )
        ) {
          day.permissionEntries.push(permission);
        }
      }

      cursor = cursor.add(1, 'day');
    }
  });

  const eventMap: CalendarEventMap = new Map();

  mutableMap.forEach((day, dateKey) => {
    const entriesByUser = new Map<number, ActivityBoardPermissionItem[]>();

    day.permissionEntries.forEach((entry) => {
      const currentEntries = entriesByUser.get(entry.assignedUserId) || [];
      currentEntries.push(entry);
      entriesByUser.set(entry.assignedUserId, currentEntries);
    });

    const permissions = Array.from(entriesByUser.entries())
      .map(([userId, entries]) => {
        const entriesAreOnThisDay = entries.every((entry) => {
          const entryStart = dayjs(entry.startDate);
          const entryEnd = dayjs(entry.endDate);

          return (
            entryStart.isValid() &&
            entryEnd.isValid() &&
            entryStart.format(DATE_KEY_FORMAT) === dateKey &&
            getPermissionEndDay(entryStart, entryEnd).format(DATE_KEY_FORMAT) === dateKey
          );
        });
        const totalHours = entriesAreOnThisDay ? entries.reduce((sum, entry) => sum + getEntryHours(entry), 0) : 0;

        return {
          assignedUserId: userId,
          nameSurname: entries[0]?.nameSurname || '-',
          entries,
          totalHours,
          isHalfDay: entriesAreOnThisDay && isHalfDayHours(totalHours),
        };
      })
      .sort((left, right) => left.nameSurname.localeCompare(right.nameSurname));

    eventMap.set(dateKey, {
      holidays: day.holidays,
      permissions,
    });
  });

  return eventMap;
};

export const buildCalendarEventMap = (
  permissionItems: ActivityBoardPermissionItem[],
  holidays: HolidayItem[],
  year: number,
  assignedUserId: number | null = null
): CalendarEventMap =>
  buildCalendarEventMapForRange(
    permissionItems,
    holidays,
    dayjs().year(year).startOf('year'),
    dayjs().year(year).endOf('year'),
    assignedUserId
  );

export const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '?';

  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toLocaleUpperCase('tr-TR');
};
